import type { ImageCrop } from '@/queries/notifications';

export interface IosFrameProps {
  state: 'banner' | 'expanded';
  title: string;
  body: string;
  appName: string;
  imageUrl?: string;
  imageCrop: ImageCrop;
  imageBgColor?: string;
}

const PADEL_ICON =
  'https://cdn.shopify.com/s/files/1/0654/5095/4474/files/logo-icon.png';

export function IosNotificationFrame({
  state,
  title,
  body,
  appName,
  imageUrl,
  imageCrop,
  imageBgColor,
}: IosFrameProps) {
  const showImage = imageCrop !== 'hidden' && Boolean(imageUrl);
  const aspectStyle = aspectRatioFor(imageCrop);

  return (
    <div style={iosOuter}>
      {/* Status bar */}
      <div style={iosStatusBar}>
        <span style={{ fontWeight: 600 }}>9:41</span>
        <span style={{ fontSize: 11, opacity: 0.9 }}>●●● 5G ⏚</span>
      </div>

      {/* Time / date hero */}
      <div style={iosLockHero}>
        <div style={iosLockDate}>الأحد 25 مايو</div>
        <div style={iosLockTime}>9:41</div>
      </div>

      {/* Notification card */}
      <div style={iosNotifCard}>
        <div style={iosNotifHeader}>
          <div style={iosAppIcon}>
            <img
              src={PADEL_ICON}
              alt=""
              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
              onError={(e) => {
                (e.currentTarget as HTMLImageElement).style.display = 'none';
              }}
            />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={iosAppName}>{appName}</div>
          </div>
          <div style={iosTimeLabel}>now</div>
        </div>

        <div style={iosNotifBody}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={iosTitle}>{title || 'عنوان الإشعار هيظهر هنا'}</div>
            <div
              style={{
                ...iosBody,
                WebkitLineClamp: state === 'banner' ? 2 : 4,
              }}
            >
              {body || 'نص الإشعار. هيظهر تحت العنوان وبيتم اقتطاعه لو طويل في الـ banner view.'}
            </div>
          </div>
          {state === 'banner' && showImage ? (
            <div style={iosBannerThumb}>
              <img
                src={imageUrl}
                alt=""
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  background: imageBgColor || 'transparent',
                }}
              />
            </div>
          ) : null}
        </div>

        {state === 'expanded' && showImage ? (
          <div
            style={{
              ...iosExpandedImageWrap,
              ...aspectStyle,
              background: imageBgColor || '#000',
            }}
          >
            <img
              src={imageUrl}
              alt=""
              style={{
                maxWidth: '90%',
                maxHeight: '90%',
                objectFit: 'contain',
              }}
            />
          </div>
        ) : null}
      </div>
    </div>
  );
}

const iosOuter: React.CSSProperties = {
  position: 'relative',
  width: '100%',
  maxWidth: 280,
  minHeight: 520,
  margin: '0 auto',
  borderRadius: 38,
  overflow: 'hidden',
  background:
    'linear-gradient(155deg, #1a1b3a 0%, #2d1b4e 35%, #1a1b3a 70%, #0d1530 100%)',
  border: '4px solid #1c1c1e',
  boxShadow:
    '0 24px 60px -20px rgba(0,0,0,0.55), inset 0 0 0 1px rgba(255,255,255,0.06)',
  fontFamily:
    '-apple-system, BlinkMacSystemFont, "SF Pro Text", "SF Pro Display", system-ui',
  color: '#fff',
};

const iosStatusBar: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  padding: '14px 22px 4px',
  fontSize: 13,
  letterSpacing: 0.2,
};

const iosLockHero: React.CSSProperties = {
  textAlign: 'center',
  padding: '20px 0 18px',
};

const iosLockDate: React.CSSProperties = {
  fontSize: 14,
  fontWeight: 500,
  opacity: 0.9,
  marginBottom: 4,
};

const iosLockTime: React.CSSProperties = {
  fontSize: 56,
  fontWeight: 200,
  letterSpacing: -1.5,
  lineHeight: 1,
};

const iosNotifCard: React.CSSProperties = {
  margin: '8px 10px 0',
  borderRadius: 18,
  background: 'rgba(255,255,255,0.18)',
  backdropFilter: 'blur(20px)',
  WebkitBackdropFilter: 'blur(20px)',
  padding: '10px 12px 12px',
  border: '0.5px solid rgba(255,255,255,0.12)',
  overflow: 'hidden',
};

const iosNotifHeader: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 6,
  marginBottom: 4,
};

const iosAppIcon: React.CSSProperties = {
  width: 18,
  height: 18,
  borderRadius: 4,
  overflow: 'hidden',
  background: '#fff',
  flexShrink: 0,
};

const iosAppName: React.CSSProperties = {
  fontSize: 12,
  fontWeight: 500,
  letterSpacing: 0.3,
  textTransform: 'uppercase',
  opacity: 0.9,
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
};

const iosTimeLabel: React.CSSProperties = {
  fontSize: 11,
  opacity: 0.7,
  flexShrink: 0,
};

const iosNotifBody: React.CSSProperties = {
  display: 'flex',
  gap: 8,
  alignItems: 'flex-start',
};

const iosTitle: React.CSSProperties = {
  fontSize: 14,
  fontWeight: 600,
  lineHeight: 1.25,
  marginBottom: 2,
  overflow: 'hidden',
  display: '-webkit-box',
  WebkitLineClamp: 1,
  WebkitBoxOrient: 'vertical' as const,
};

const iosBody: React.CSSProperties = {
  fontSize: 13,
  fontWeight: 400,
  lineHeight: 1.3,
  opacity: 0.92,
  overflow: 'hidden',
  display: '-webkit-box',
  WebkitBoxOrient: 'vertical' as const,
};

const iosBannerThumb: React.CSSProperties = {
  width: 38,
  height: 38,
  borderRadius: 6,
  overflow: 'hidden',
  flexShrink: 0,
};

const iosExpandedImageWrap: React.CSSProperties = {
  marginTop: 10,
  borderRadius: 10,
  overflow: 'hidden',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '8%',
};

function aspectRatioFor(crop: ImageCrop): React.CSSProperties {
  switch (crop) {
    case 'square':
      return { aspectRatio: '1 / 1' };
    case 'landscape':
      return { aspectRatio: '16 / 9' };
    case 'portrait':
      return { aspectRatio: '3 / 4' };
    case 'hidden':
      return { display: 'none' };
    case 'auto':
    default:
      return { aspectRatio: '3 / 2' };
  }
}
